import { Icon } from 'design-system';
import { HStack, IconButtonWithTooltip, Input, Td, Text, Tr } from 'design-system';
import type { DealCategoryTreeNode } from 'apis/supplier-prices';
import { useMemo } from 'react';
import { FiChevronDown, FiChevronUp, FiEdit, FiTrash2 } from 'react-icons/fi';
import { findCategoryPath } from 'components/category-picker/category-tree-helpers';
import { CategoryModal } from 'components/category-picker/category-modal';
import type { EditableFamilyRow } from './editable-family-row.types';

interface Props {
  row: EditableFamilyRow;
  index: number;
  invalid: boolean;
  categoryTree: DealCategoryTreeNode[];
  onChange: (clientId: string, patch: Partial<EditableFamilyRow>) => void;
  onInsert: (index: number, position: 'above' | 'below') => void;
  onRemove: (index: number) => void;
}

export const EditableFamilyRowView = ({ row, index, invalid, categoryTree, onChange, onInsert, onRemove }: Props) => {
  const canRemove = row.id === undefined; // only new rows are removable

  const breadcrumb = useMemo(() => {
    if (row.category == null) return '';
    const path = findCategoryPath(categoryTree, row.category.id);
    if (path.length) return path.map((n) => n.title || `Untitled (${n.id})`).join(' › ');
    return row.category.title || `#${row.category.id}`;
  }, [categoryTree, row.category]);

  return (
    <Tr bg={invalid ? 'red.50' : undefined}>
      <Td>
        <Input
          size='sm'
          w='220px'
          value={row.universalName}
          isInvalid={invalid && !row.universalName.trim()}
          onChange={(e) => onChange(row.clientId, { universalName: e.target.value })}
        />
      </Td>
      <Td>
        <HStack spacing='1' align='center' minW='200px' maxW='320px'>
          <Text
            flex='1'
            fontSize='xs'
            color={row.category == null ? (invalid ? 'red.500' : 'gray.400') : 'gray.600'}
            whiteSpace='normal'
            lineHeight='short'>
            {row.category == null ? 'No category' : breadcrumb}
          </Text>
          <CategoryModal
            value={row.category}
            onSelect={(category) => onChange(row.clientId, { category })}
            trigger={
              <IconButtonWithTooltip
                size='xs'
                variant='ghost'
                label='Edit category'
                aria-label='edit-family-category-btn'
                Icon={<Icon as={FiEdit} />}
              />
            }
          />
        </HStack>
      </Td>
      <Td whiteSpace='nowrap'>
        <IconButtonWithTooltip
          size='xs'
          variant='ghost'
          label='Add row above'
          aria-label='add-family-row-above'
          Icon={<Icon as={FiChevronUp} />}
          onClick={() => onInsert(index, 'above')}
        />
        <IconButtonWithTooltip
          size='xs'
          variant='ghost'
          label='Add row below'
          aria-label='add-family-row-below'
          Icon={<Icon as={FiChevronDown} />}
          onClick={() => onInsert(index, 'below')}
        />
        {canRemove && (
          <IconButtonWithTooltip
            size='xs'
            variant='ghost'
            colorScheme='red'
            label='Remove row'
            aria-label='remove-family-row'
            Icon={<Icon as={FiTrash2} />}
            onClick={() => onRemove(index)}
          />
        )}
      </Td>
    </Tr>
  );
};
